import { useState } from "react";
import { isManualSeason } from "@averagejoesff/shared";
import type { SeasonArchive } from "@averagejoesff/shared";
import { useManagerNames } from "../../lib/useManagerNames";
import { Tabs } from "../../components/Tabs";
import { useLeagueDocs } from "../../lib/LeagueDocsProvider";
import { ClearSeasonResults } from "./ClearSeasonResults";
import { EditManagerNamesForm } from "./EditManagerNamesForm";
import { EditPayoutWinnersForm } from "./EditPayoutWinnersForm";
import { EditTeamsForm } from "./EditTeamsForm";
import { EditWeekForm } from "./EditWeekForm";

interface AdminPanelProps {
  season: number;
  archive: SeasonArchive;
  idToken: string;
  /** Called with the freshly-saved archive so the caller can update state directly. */
  onSaved: (archive: SeasonArchive) => void;
}

type Tab = "week" | "teams" | "managers" | "prizes";

/** `manualOnly` tabs rewrite the season itself, which only a hand-entered season allows. */
const ALL_TABS: { id: Tab; label: string; manualOnly: boolean }[] = [
  { id: "week", label: "Add/edit a week", manualOnly: true },
  { id: "teams", label: "Manage teams", manualOnly: true },
  { id: "managers", label: "Manager names", manualOnly: false },
  { id: "prizes", label: "Prizes", manualOnly: false },
];

export function AdminPanel({ season, archive, idToken, onSaved }: AdminPanelProps) {
  // A Yahoo season's results and roster belong to the weekly fetch (see
  // `isManualSeason`); the API refuses those edits, so don't offer them.
  const editable = isManualSeason(archive);
  const tabs = ALL_TABS.filter((t) => editable || !t.manualOnly);
  const [selectedTab, setTab] = useState<Tab>(tabs[0]!.id);
  // The panel stays open across the season picker, so a tab picked on a
  // hand-entered season may not exist on the next one.
  const tab = tabs.some((t) => t.id === selectedTab) ? selectedTab : tabs[0]!.id;
  const weeks = archive.awards.biggestBlowout?.weeks ?? [];
  const { aliases, setAliases } = useManagerNames(archive);
  const { payouts, payoutWinners, setPayoutWinners } = useLeagueDocs();

  return (
    <section className="admin-panel" aria-label="Edit season data">
      <Tabs tabs={tabs} active={tab} onChange={setTab} block="admin-panel" />

      {!editable && (
        <p className="admin-panel__hint">
          The {season} season is synced from Yahoo, so its results and teams update automatically
          each week and can't be edited here.
        </p>
      )}

      {tab === "week" && (
        <EditWeekForm
          season={season}
          teams={archive.teams}
          weeks={weeks}
          idToken={idToken}
          onSaved={onSaved}
        >
          <ClearSeasonResults season={season} idToken={idToken} onCleared={onSaved} />
        </EditWeekForm>
      )}
      {tab === "teams" && (
        <EditTeamsForm season={season} teams={archive.teams} idToken={idToken} onSaved={onSaved}>
          <ClearSeasonResults season={season} idToken={idToken} onCleared={onSaved} />
        </EditTeamsForm>
      )}
      {tab === "managers" && aliases.status === "ready" && (
        <EditManagerNamesForm
          season={season}
          teams={archive.teams}
          currentAliases={aliases.data}
          idToken={idToken}
          onSaved={setAliases}
        />
      )}
      {tab === "managers" && aliases.status === "loading" && (
        <p className="admin-panel__status">Loading manager names…</p>
      )}
      {tab === "managers" && aliases.status === "error" && (
        <p className="admin-panel__status admin-panel__status--error">
          Could not load manager names: {aliases.error.message}
        </p>
      )}
      {tab === "prizes" &&
        payouts.status === "ready" &&
        payouts.data &&
        payoutWinners.status === "ready" && (
          <EditPayoutWinnersForm
            season={season}
            teams={archive.teams}
            structure={payouts.data}
            currentWinners={payoutWinners.data}
            idToken={idToken}
            onSaved={setPayoutWinners}
          />
        )}
      {tab === "prizes" && payouts.status === "ready" && payouts.data === null && (
        <p className="admin-panel__hint">
          No payout structure published yet — set one up on the Prize Payouts page first.
        </p>
      )}
      {tab === "prizes" && (payouts.status === "loading" || payoutWinners.status === "loading") && (
        <p className="admin-panel__status">Loading prize winners…</p>
      )}
      {tab === "prizes" && payouts.status === "error" && (
        <p className="admin-panel__status admin-panel__status--error">
          Could not load the payout structure: {payouts.error.message}
        </p>
      )}
      {tab === "prizes" && payoutWinners.status === "error" && (
        <p className="admin-panel__status admin-panel__status--error">
          Could not load prize winners: {payoutWinners.error.message}
        </p>
      )}
    </section>
  );
}
